import type { PropsWithChildren, ReactNode } from "react";
import {
  IconNameEnum,
  useModuleBEM,
  useUiStyles,
} from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { noop } from "@hopper-b2b/utilities";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Icon } from "@tenant/ui";
import defaultStyles from "./Dialog.module.scss";

export interface DialogHeaderProps {
  showCloseButton?: boolean;
  title?: ReactNode;
  titleIsVisuallyHidden?: boolean;
  onClose?: () => void;
  /**
   * @deprecated See Dialog component for more details
   */
  showHeaderBottomBorder?: boolean;
  /**
   * @deprecated See Dialog component for more details
   */
  showBackButton?: boolean;
}

export const DialogHeader = ({
  showCloseButton = true,
  title,
  titleIsVisuallyHidden,
  onClose,
  showHeaderBottomBorder = true,
  showBackButton = false,
}: DialogHeaderProps) => {
  // Keep this as Dialog to avoid breaking existing overrides
  const styles = useUiStyles(CoreUiComponents.Dialog, defaultStyles);
  const [block, cn] = useModuleBEM(styles, CoreUiComponents.Dialog);

  const { t } = useI18nContext();

  const backButton = (
    <CloseButton
      label={t("back")}
      className={cn("button", { left: true })}
      onClose={onClose ?? noop}
    >
      <Icon iconName={IconNameEnum.backIcon} />
    </CloseButton>
  );

  const closeButton = (
    <CloseButton
      label={t("close.button")}
      className={cn("button", { right: true })}
      onClose={onClose ?? noop}
    >
      <Icon iconName={IconNameEnum.close} />
    </CloseButton>
  );

  let titleComponent: ReactNode;
  if (title == null) {
    console.warn(
      "`DialogContent` requires a `DialogTitle` for the component to be accessible for screen reader users. For now we give a placeholder but it should be fixed."
    );
    titleComponent = (
      <VisuallyHidden asChild>
        <DialogPrimitive.Title>{"Dialog title"}</DialogPrimitive.Title>
      </VisuallyHidden>
    );
  } else if (titleIsVisuallyHidden === true) {
    titleComponent = (
      <VisuallyHidden asChild>
        <DialogPrimitive.Title className={cn("title")}>
          {title}
        </DialogPrimitive.Title>
      </VisuallyHidden>
    );
  } else {
    titleComponent = (
      <DialogPrimitive.Title className={cn("title")}>
        {title}
      </DialogPrimitive.Title>
    );
  }

  return (
    <div className={cn("header", { bottomBorder: !!showHeaderBottomBorder })}>
      {showBackButton && backButton}
      {titleComponent}
      {showCloseButton && closeButton}
    </div>
  );
};

export type CLoseButtonProps = PropsWithChildren<{
  onClose?: () => void;
  className?: string;
  label: string;
}>;

export const CloseButton = ({
  children,
  onClose,
  className,
  label,
}: CLoseButtonProps) => {
  return (
    <DialogPrimitive.Close asChild>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        className={className}
        aria-label={label}
      >
        {children}
      </button>
    </DialogPrimitive.Close>
  );
};
